/**
 * The Alpha DNA card, drawn at runtime from a CardConfig (cardDesign.ts) so the
 * customize panel can change it live. Two styles:
 *
 * - Stealth: a metal card (Robinhood Gold / Amex Centurion). The face stays
 *   dark and plain; the symbol is laser-etched (matte, slightly recessed, with
 *   a crisp lip that catches the light), the type is printed, and the light
 *   mostly shows on the brushed metal and the rim.
 * - Player: a printed, lacquered card (Sorare / FUT). The emblem or initials in a
 *   tier-coloured glow, the rating top-left, a big name at the bottom; only the
 *   Elite tier gets a holo sheen.
 *
 * Deliberately no texture relief or sharp clearcoat on big areas: those turned
 * every passing light into noise and hot spots.
 */
import * as THREE from 'three';
import { initials, SAMPLE_STATS } from './cardDesign';

export const CARD_W = 0.7;
export const CARD_H = 0.98;
const DEPTH = 0.012;

// face art is laid out on a 300 x 420 design grid and drawn at K times that
const DESIGN_W = 300;
const DESIGN_H = 420;
const FACE_W = 1024;
const FACE_H = Math.round((FACE_W * DESIGN_H) / DESIGN_W);
// relief + surface maps at a lower resolution (they are smooth, and cheaper to recompute)
const MAP_W = 768;
const MAP_H = Math.round((MAP_W * DESIGN_H) / DESIGN_W);

const MARK = [
  'M11.151 0C11.1754 0.014262 11.2053 0.0213929 11.2184 0.0392204C11.2633 0.103399 11.3027 0.172926 11.342 0.238888C14.2825 5.16818 17.2231 10.0957 20.1637 15.0232C20.8394 16.1552 21.5151 17.2873 22.1908 18.4211C22.2357 18.496 22.2769 18.5726 22.3199 18.6493C22.3368 18.6796 22.3106 18.7259 22.2788 18.7295C22.2525 18.7331 22.2245 18.7402 22.1982 18.7402C22.1253 18.742 22.0542 18.742 21.9811 18.742C19.2427 18.742 16.5062 18.742 13.7678 18.742C13.6779 18.742 13.5862 18.7384 13.4964 18.7349C13.4477 18.7331 13.4252 18.6849 13.4496 18.6403C13.4627 18.6172 13.4739 18.594 13.487 18.5709C13.5357 18.4888 13.5862 18.4051 13.6349 18.3231C14.0504 17.626 14.4678 16.929 14.8834 16.2301C14.9152 16.1784 14.947 16.1249 14.9788 16.0732C15.0125 16.0162 15.0593 15.9841 15.1323 15.9841C15.2147 15.9859 15.2951 15.9823 15.3775 15.9823C15.9578 15.9823 16.538 15.9823 17.1183 15.9823C17.1894 15.9823 17.2624 15.9734 17.3335 15.9645C17.3448 15.9627 17.3578 15.9413 17.3597 15.9253C17.3616 15.9092 17.3578 15.8914 17.3504 15.8771C17.3167 15.8165 17.283 15.7541 17.2474 15.6953C15.2914 12.4168 13.3354 9.14011 11.3794 5.86164C11.3307 5.77965 11.2802 5.69765 11.2278 5.61565C11.1922 5.5604 11.1398 5.56217 11.1024 5.62277C11.0556 5.69588 11.0088 5.77076 10.9657 5.8456C9.97181 7.51071 8.97793 9.17578 7.984 10.8409C7.93907 10.9157 7.88852 10.987 7.84173 11.0601C7.82866 11.0798 7.78745 11.0744 7.77247 11.053C7.74252 11.0102 7.71261 10.9675 7.68638 10.9228C7.21095 10.1278 6.73739 9.33266 6.26196 8.53757C6.23949 8.50013 6.21703 8.46269 6.19647 8.42348C6.16838 8.37353 6.1721 8.32362 6.20019 8.27548C6.23391 8.21488 6.26945 8.15428 6.30503 8.09367C7.00319 6.92417 7.70136 5.75472 8.39952 4.58344C9.25866 3.14298 10.1178 1.70252 10.977 0.263846C11.0313 0.178274 11.0762 0.0837888 11.151 0Z',
  'M12.8035 13.9875C12.7998 14.0035 12.7979 14.0231 12.7904 14.0374C12.7624 14.0909 12.7305 14.1443 12.7006 14.1961C11.1302 16.8291 9.55786 19.4623 7.98558 22.0936C7.94066 22.1685 7.89014 22.2398 7.84145 22.3111C7.82838 22.3307 7.78531 22.3289 7.77033 22.3093C7.72727 22.2434 7.68238 22.1792 7.64118 22.1114C7.20691 21.3841 6.77269 20.6567 6.33842 19.9293C5.7563 18.9542 5.17418 17.979 4.59019 17.0057C4.55275 16.9432 4.53404 16.8577 4.44232 16.8363C4.36745 16.8648 4.34873 16.9379 4.31317 16.9967C4.02491 17.4763 3.73853 17.9559 3.45215 18.4354C3.42969 18.4729 3.40723 18.5103 3.38477 18.5477C3.26872 18.7402 3.27808 18.742 3.04785 18.742C2.09511 18.742 1.14425 18.742 0.191519 18.742C0.155955 18.742 0.11852 18.7402 0.0829562 18.7385C0.00995678 18.7349 -0.0181199 18.6957 0.0118286 18.6404C0.0492642 18.5727 0.0885714 18.5031 0.12975 18.4372C0.773643 17.3568 1.41753 16.2765 2.06329 15.1962C2.80264 13.9589 3.54013 12.7217 4.27948 11.4845C4.3244 11.4096 4.37493 11.3383 4.42547 11.267C4.43109 11.2599 4.46478 11.2599 4.4704 11.267C4.51158 11.3223 4.55275 11.3793 4.58832 11.4399C4.95144 12.0478 5.31454 12.654 5.67584 13.2619C6.32907 14.3565 6.98231 15.4529 7.63931 16.5457C7.6861 16.6224 7.69922 16.7311 7.81712 16.7578C7.90321 16.7133 7.92759 16.6241 7.97061 16.5528C8.95704 14.9038 9.94157 13.253 10.9262 11.6021C10.9804 11.513 11.0328 11.4221 11.0871 11.3312C11.1452 11.2367 11.1807 11.2349 11.2369 11.324C11.2556 11.3543 11.2743 11.3829 11.2912 11.4132C11.761 12.2011 12.2308 12.9873 12.7006 13.7753C12.7324 13.827 12.7624 13.8805 12.7904 13.934C12.8017 13.9518 12.8017 13.9714 12.8035 13.9875Z',
].map((d) => new Path2D(d));

const DISPLAY = '"Clash Display", Inter, sans-serif';
const SANS = 'Inter, "Helvetica Neue", Arial, sans-serif';
const ID_BLUE = '#2a66ff'; // saturated Alpha blue (the page uses #1a5dfd); a paler blue went grey under the warm lights

/** Stealth metal per tier: Silver = titanium, Gold = royal-blue metal with gold, Elite = solid gold. */
const STEALTH = {
  Silver: {
    base: ['#343941', '#1b1e23', '#292d34'],
    ink: '#e3e8ef',
    ink2: 'rgba(227,232,239,0.56)',
    id: ID_BLUE,
    etch: '#24282e',
    rim: '#c9d1dc',
    metal: 0.8,
    rough: 0.34,
    env: 0.75,
    grain: 0.05,
  },
  // Gold: deep royal-blue metal with gold print and rim (the near-black stock read as a dull grey-blue)
  Gold: {
    base: ['#1a3b92', '#0a1f5d', '#15337e'],
    ink: '#e3c98f',
    ink2: 'rgba(222,186,116,0.85)',
    id: '#ffffff',
    etch: '#d3dbea',
    rim: '#e4c27f',
    metal: 0.4,
    rough: 0.44,
    env: 0.3,
    grain: 0.06,
  },
  Elite: {
    base: ['#ead4a3', '#b8945a', '#d9bd85'],
    ink: '#2a2014',
    ink2: 'rgba(42,32,20,0.62)',
    id: '#1a3d9e',
    etch: '#a98a52',
    rim: '#f0d79c',
    metal: 0.85,
    rough: 0.3,
    env: 0.8,
    grain: 0.08,
  },
};

/** Player card per tier: the glow colour is the tier; Elite adds a holo sheen. */
const PLAYER = {
  Silver: {
    bg: ['#272e38', '#0a0c10'],
    glow: 'rgba(176,192,214,0.30)',
    ink: '#eef2f7',
    sub: 'rgba(238,242,247,0.55)',
    acc: '#c3ccd8',
    line: 'rgba(195,204,216,0.3)',
    rim: '#aeb8c6',
    holo: 0,
  },
  Gold: {
    bg: ['#2a2112', '#0e0b07'],
    glow: 'rgba(214,176,106,0.32)',
    ink: '#f6ead2',
    sub: 'rgba(246,234,210,0.55)',
    acc: '#d6b06a',
    line: 'rgba(214,176,106,0.3)',
    rim: '#d6b06a',
    holo: 0,
  },
  Elite: {
    bg: ['#0f2366', '#050914'],
    glow: 'rgba(77,125,255,0.45)',
    ink: '#eef3ff',
    sub: 'rgba(238,243,255,0.55)',
    acc: '#8fb0ff',
    line: 'rgba(143,176,255,0.3)',
    rim: '#8fb0ff',
    holo: 0.1,
  },
};

/** Brushed-metal streaks: a fixed noise across the card, constant down it. */
const STREAKS = (() => {
  let seed = 7;
  const rnd = () => (seed = (seed * 16807) % 2147483647) / 2147483647;
  const out = new Float32Array(2048);
  for (const [len, amp] of [
    [1, 0.5],
    [3, 0.3],
    [9, 0.2],
  ]) {
    let v = 0;
    for (let i = 0; i < out.length; i++) {
      if (i % len === 0) v = rnd() * 2 - 1;
      out[i] += v * amp;
    }
  }
  return out;
})();
const streak = (u) => STREAKS[Math.min(STREAKS.length - 1, Math.max(0, Math.floor(u * STREAKS.length)))];

// ------------------------------------------------------------------ shared helpers
function canvas(w, h) {
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  return [c, c.getContext('2d', { willReadFrequently: true })];
}

function shapePath(w, h, cut, inset = 0) {
  // same outline as the design board, in the design grid, scaled by the caller
  const p = new Path2D();
  const r = 18 - inset * 0.5;
  const c = 30;
  const rc = 6;
  const L = inset,
    T = inset,
    R = w - inset,
    B = h - inset;
  if (!cut) {
    p.moveTo(L + r, T);
    p.lineTo(R - r, T);
    p.arcTo(R, T, R, T + r, r);
    p.lineTo(R, B - r);
    p.arcTo(R, B, R - r, B, r);
    p.lineTo(L + r, B);
    p.arcTo(L, B, L, B - r, r);
    p.lineTo(L, T + r);
    p.arcTo(L, T, L + r, T, r);
  } else {
    p.moveTo(L + c + rc, T);
    p.lineTo(R - c - rc, T);
    p.quadraticCurveTo(R - c, T, R - c + rc * 0.7, T + rc * 0.7);
    p.lineTo(R - rc * 0.7, T + c - rc * 0.7);
    p.quadraticCurveTo(R, T + c, R, T + c + rc);
    p.lineTo(R, B - r);
    p.arcTo(R, B, R - r, B, r);
    p.lineTo(L + r, B);
    p.arcTo(L, B, L, B - r, r);
    p.lineTo(L, T + c + rc);
    p.quadraticCurveTo(L, T + c, L + rc * 0.7, T + c - rc * 0.7);
    p.lineTo(L + c - rc * 0.7, T + rc * 0.7);
    p.quadraticCurveTo(L + c, T, L + c + rc, T);
  }
  p.closePath();
  return p;
}

/** The same outline as a THREE.Shape in metres (card centred on the origin). */
function cardShape(cut) {
  const sx = CARD_W / DESIGN_W;
  const sy = CARD_H / DESIGN_H;
  const X = (x) => x * sx - CARD_W / 2;
  const Y = (y) => CARD_H / 2 - y * sy;
  const s = new THREE.Shape();
  const r = 18,
    c = 30,
    rc = 6,
    w = DESIGN_W,
    h = DESIGN_H;
  const arc = (cx, cy, a0, a1, rad) => {
    for (let i = 0; i <= 8; i++) {
      const a = a0 + ((a1 - a0) * i) / 8;
      s.lineTo(X(cx + rad * Math.cos(a)), Y(cy + rad * Math.sin(a)));
    }
  };
  if (!cut) {
    s.moveTo(X(r), Y(0));
    s.lineTo(X(w - r), Y(0));
    arc(w - r, r, -Math.PI / 2, 0, r);
    s.lineTo(X(w), Y(h - r));
    arc(w - r, h - r, 0, Math.PI / 2, r);
    s.lineTo(X(r), Y(h));
    arc(r, h - r, Math.PI / 2, Math.PI, r);
    s.lineTo(X(0), Y(r));
    arc(r, r, Math.PI, Math.PI * 1.5, r);
  } else {
    s.moveTo(X(c + rc), Y(0));
    s.lineTo(X(w - c - rc), Y(0));
    s.quadraticCurveTo(X(w - c), Y(0), X(w - c + rc * 0.7), Y(rc * 0.7));
    s.lineTo(X(w - rc * 0.7), Y(c - rc * 0.7));
    s.quadraticCurveTo(X(w), Y(c), X(w), Y(c + rc));
    s.lineTo(X(w), Y(h - r));
    arc(w - r, h - r, 0, Math.PI / 2, r);
    s.lineTo(X(r), Y(h));
    arc(r, h - r, Math.PI / 2, Math.PI, r);
    s.lineTo(X(0), Y(c + rc));
    s.quadraticCurveTo(X(0), Y(c), X(rc * 0.7), Y(c - rc * 0.7));
    s.lineTo(X(c - rc * 0.7), Y(rc * 0.7));
    s.quadraticCurveTo(X(c), Y(0), X(c + rc), Y(0));
  }
  return s;
}

function cardGeometry(cut) {
  const geo = new THREE.ExtrudeGeometry(cardShape(cut), {
    depth: DEPTH,
    bevelEnabled: true,
    bevelThickness: 0.0025,
    bevelSize: 0.0025,
    bevelSegments: 3,
    curveSegments: 8,
  });
  geo.translate(0, 0, -DEPTH / 2);
  // ExtrudeGeometry gives the caps world-unit UVs; normalise them to the face
  const pos = geo.attributes.position;
  const uv = geo.attributes.uv;
  for (let i = 0; i < uv.count; i++) uv.setXY(i, pos.getX(i) / CARD_W + 0.5, pos.getY(i) / CARD_H + 0.5);
  uv.needsUpdate = true;
  return geo;
}

let fontsReady = null;
export function loadFonts() {
  fontsReady ??= (async () => {
    try {
      for (const [w, f] of [
        ['600', 'Semibold'],
        ['500', 'Medium'],
      ]) {
        const face = new FontFace('Clash Display', `url(/3d/alpha-dna/fonts/ClashDisplay-${f}.otf)`, { weight: w });
        document.fonts.add(await face.load());
      }
      await Promise.all(['700 40px Inter', '600 20px Inter', '500 20px Inter'].map((f) => document.fonts.load(f)));
    } catch {
      /* fall back to whatever is available */
    }
  })();
  return fontsReady;
}

function withAlpha(hex, a) {
  const c = new THREE.Color(hex);
  return `rgba(${Math.round(c.r * 255)},${Math.round(c.g * 255)},${Math.round(c.b * 255)},${a})`;
}

function spaced(ctx, em, size) {
  // canvas letterSpacing where supported (Chrome, Safari 17.4+); harmless elsewhere
  ctx.letterSpacing = `${em * size}px`;
}

function fitFont(ctx, text, weight, size, family, max) {
  let s = size;
  ctx.font = `${weight} ${s}px ${family}`;
  while (s > 10 && ctx.measureText(text).width > max) {
    s -= 0.5;
    ctx.font = `${weight} ${s}px ${family}`;
  }
  return s;
}

function wordmark(ctx, x, y, size, ink, id) {
  ctx.textAlign = 'left';
  ctx.textBaseline = 'middle';
  ctx.font = `600 ${size}px ${DISPLAY}`;
  spaced(ctx, 0.06, size);
  ctx.fillStyle = ink;
  ctx.fillText('ALPHA', x, y);
  const w = ctx.measureText('ALPHA').width;
  ctx.fillStyle = id;
  ctx.fillText('DNA', x + w, y);
}

/** The outline along the card edge, brightest at the top-left and bottom-right like a lit bevel. */
function outline(ctx, cut, color, face) {
  if (face) {
    const g = ctx.createLinearGradient(0, 0, DESIGN_W, DESIGN_H);
    g.addColorStop(0, withAlpha(color, 1));
    g.addColorStop(0.35, withAlpha(color, 0.28));
    g.addColorStop(0.7, withAlpha(color, 0.16));
    g.addColorStop(1, withAlpha(color, 0.85));
    ctx.strokeStyle = g;
  } else {
    ctx.strokeStyle = '#fff';
  }
  ctx.lineWidth = 2.2;
  ctx.stroke(shapePath(DESIGN_W, DESIGN_H, cut, 1.1));
}

function drawMark(ctx, cx, cy, size) {
  ctx.save();
  ctx.translate(cx - size / 2, cy - size / 2);
  ctx.scale(size / 23, size / 23);
  for (const p of MARK) ctx.fill(p);
  ctx.restore();
}

// ---- Stealth metal
const ST = { y: 136, mark: 104, mono: 82 }; // the symbol sits high: the ratings take the lower half

const drawStealth = (ctx, d, mode) => {
  const { cfg } = d;
  const t = STEALTH[cfg.tier];
  const s = SAMPLE_STATS[cfg.tier];
  const face = mode === 'face';
  const ink = (c) => (face ? c : '#fff');

  if (face) {
    const g = ctx.createLinearGradient(110, 0, 190, DESIGN_H);
    g.addColorStop(0, t.base[0]);
    g.addColorStop(0.55, t.base[1]);
    g.addColorStop(1, t.base[2]);
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, DESIGN_W, DESIGN_H);
    // brushed streaks, faint in the colour; the light brings out the rest
    for (let x = 0; x < DESIGN_W; x += 0.5) {
      const v = streak(x / DESIGN_W);
      ctx.fillStyle =
        v > 0 ? `rgba(255,255,255,${(v * t.grain).toFixed(3)})` : `rgba(0,0,0,${(-v * t.grain * 1.6).toFixed(3)})`;
      ctx.fillRect(x, 0, 0.5, DESIGN_H);
    }
  }

  // ---- the etched symbol (a faint lip below, a shadow above, like the board's drop-shadows)
  if (mode !== 'ink') {
    const shape = (fill, dy) => {
      ctx.fillStyle = fill;
      if (cfg.symbol === 'Monogram') {
        ctx.font = `600 ${ST.mono}px ${DISPLAY}`;
        spaced(ctx, 0, ST.mono);
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(initials(cfg.name), DESIGN_W / 2, ST.y + dy + 2);
      } else {
        drawMark(ctx, DESIGN_W / 2, ST.y + dy, ST.mark);
      }
    };
    if (face) {
      shape(cfg.tier === 'Elite' ? 'rgba(255,240,205,0.7)' : withAlpha(t.rim, 0.22), 0.9);
      shape(cfg.tier === 'Elite' ? 'rgba(60,40,10,0.45)' : 'rgba(0,0,0,0.55)', -0.9);
      shape(t.etch, 0);
    } else {
      shape('#fff', 0);
    }
  }
  if (mode === 'etch') return;

  // ---- printed type
  wordmark(ctx, 28, 37, 12, ink(t.ink), ink(t.id));
  ctx.textAlign = 'right';
  ctx.font = `700 8.5px ${SANS}`;
  spaced(ctx, 0.22, 8.5);
  ctx.fillStyle = ink(t.ink2);
  ctx.fillText(cfg.tier.toUpperCase(), DESIGN_W - 28 + 1.9, 37.5);

  // the ratings, centred under the symbol: the overall big, a hairline, then one row of the four
  // scores (full name over a large number, hairlines between). Columns are close to equal, each
  // nudged toward its content's width, so CONSISTENCY fits and EDGE / RISK don't crowd.
  const cx = DESIGN_W / 2;
  // labels in a soft white on the blue Gold card (the numbers carry the gold); elsewhere the print tone
  const labelInk = ink(cfg.tier === 'Gold' ? 'rgba(236,241,252,0.78)' : t.ink2);
  // canvas letterSpacing also pads after the last letter: nudge centred labels by half of it
  const label = (text, x, y, size, track) => {
    ctx.font = `600 ${size}px ${SANS}`;
    spaced(ctx, track, size);
    ctx.fillStyle = labelInk;
    ctx.fillText(text, x + (track * size) / 2, y);
  };
  ctx.textBaseline = 'alphabetic';
  ctx.textAlign = 'center';
  label('OVERALL', cx, 224, 10.5, 0.24);
  ctx.font = `500 56px ${DISPLAY}`;
  spaced(ctx, -0.02, 56);
  ctx.fillStyle = ink(t.ink);
  ctx.fillText(String(s.overall), cx, 280);
  const hair = face ? 'rgba(255,255,255,0.3)' : '#fff'; // thin white rules: gold ones read as dark lines on the blue
  ctx.fillStyle = hair;
  ctx.fillRect(24, 302, DESIGN_W - 48, 0.8);

  const ROW = { label: 9, track: 0.07, value: 28, labelY: 338, valueY: 376, left: 16, right: DESIGN_W - 16 };
  const cols = s.stats.map(([name, , v]) => {
    ctx.font = `600 ${ROW.label}px ${SANS}`;
    spaced(ctx, ROW.track, ROW.label);
    const lw = ctx.measureText(name).width;
    ctx.font = `500 ${ROW.value}px ${DISPLAY}`;
    spaced(ctx, 0, ROW.value);
    return { name, v, w: Math.max(lw, ctx.measureText(String(v)).width) };
  });
  const equal = (ROW.right - ROW.left) / cols.length;
  const meanW = cols.reduce((sum, c) => sum + c.w, 0) / cols.length;
  let x = ROW.left;
  cols.forEach((c, i) => {
    const colW = equal + 0.6 * (c.w - meanW);
    const mid = x + colW / 2;
    if (i > 0) {
      ctx.fillStyle = face ? 'rgba(255,255,255,0.42)' : '#fff';
      ctx.fillRect(x - 0.5, 324, 1, 58);
    }
    label(c.name, mid, ROW.labelY, ROW.label, ROW.track);
    ctx.font = `500 ${ROW.value}px ${DISPLAY}`;
    spaced(ctx, 0, ROW.value);
    ctx.fillStyle = ink(t.ink);
    ctx.fillText(String(c.v), mid, ROW.valueY);
    x += colW;
  });
  ctx.textAlign = 'left';

  outline(ctx, cfg.shape === 'Cut corners', t.rim, face);
};

// ---- Player card
const PL = { glowY: 151, mark: 196, mono: 132, heroY: 172 };

const drawPlayer = (ctx, d, mode) => {
  if (mode === 'etch') return; // printed: nothing is etched
  const { cfg } = d;
  const t = PLAYER[cfg.tier];
  const s = SAMPLE_STATS[cfg.tier];
  const face = mode === 'face';
  const ink = (c) => (face ? c : '#fff');

  if (face) {
    const bg = ctx.createLinearGradient(0, 0, 70, DESIGN_H);
    bg.addColorStop(0, t.bg[0]);
    bg.addColorStop(0.7, t.bg[1]);
    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, DESIGN_W, DESIGN_H);
    const glow = ctx.createRadialGradient(DESIGN_W / 2, PL.glowY, 0, DESIGN_W / 2, PL.glowY, 180);
    glow.addColorStop(0, t.glow);
    glow.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.fillStyle = glow;
    ctx.fillRect(0, 0, DESIGN_W, DESIGN_H);

    // ---- the hero: the emblem or the initials, glowing in the tier colour
    const g = ctx.createLinearGradient(0, PL.heroY - 100, 0, PL.heroY + 100);
    g.addColorStop(0, withAlpha(t.acc, 0.9));
    g.addColorStop(1, withAlpha(t.acc, 0.08));
    ctx.save();
    ctx.shadowColor = withAlpha(t.acc, 0.55);
    ctx.shadowBlur = 26;
    ctx.fillStyle = g;
    if (cfg.symbol === 'Monogram') {
      ctx.font = `600 ${PL.mono}px ${DISPLAY}`;
      spaced(ctx, -0.02, PL.mono);
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(initials(cfg.name), DESIGN_W / 2, PL.heroY + 4);
    } else {
      drawMark(ctx, DESIGN_W / 2 + 6, PL.heroY, PL.mark);
    }
    ctx.restore();
    // fade the hero into the card so the name sits on a clean field
    const fade = ctx.createLinearGradient(0, 168, 0, 311);
    fade.addColorStop(0, withAlpha(t.bg[1], 0));
    fade.addColorStop(1, t.bg[1]);
    ctx.fillStyle = fade;
    ctx.fillRect(0, 168, DESIGN_W, DESIGN_H - 168);
  }

  // ---- rating, top-left
  ctx.textAlign = 'left';
  ctx.textBaseline = 'alphabetic';
  ctx.font = `600 46px ${DISPLAY}`;
  spaced(ctx, -0.03, 46);
  ctx.fillStyle = ink(t.ink);
  ctx.fillText(String(s.overall), 23, 64);
  ctx.font = `700 8.5px ${SANS}`;
  spaced(ctx, 0.2, 8.5);
  ctx.fillStyle = ink(t.acc);
  ctx.fillText(cfg.tier.toUpperCase(), 25, 82);

  // ---- brand on the spine
  ctx.save();
  ctx.translate(DESIGN_W - 22, 26);
  ctx.rotate(Math.PI / 2);
  ctx.textBaseline = 'middle';
  ctx.font = `600 10.5px ${DISPLAY}`;
  spaced(ctx, 0.3, 10.5);
  ctx.globalAlpha = face ? 0.78 : 1;
  ctx.fillStyle = ink(t.ink);
  ctx.fillText('ALPHA', 0, 0);
  let x = ctx.measureText('ALPHA').width;
  ctx.fillStyle = ink(ID_BLUE);
  ctx.fillText('ID', x, 0);
  x += ctx.measureText('ID').width;
  ctx.fillStyle = ink(t.ink);
  ctx.fillText(' · S01', x, 0);
  ctx.restore();

  // ---- the name, big, on one or two lines
  const words = (cfg.name || 'Your Name').trim().toUpperCase().split(/\s+/);
  const lines = words.length > 1 ? [words[0], words.slice(1).join(' ')] : words;
  spaced(ctx, -0.01, 34);
  const size = Math.min(...lines.map((l) => fitFont(ctx, l, 600, 34, DISPLAY, DESIGN_W - 48)));
  ctx.font = `600 ${size}px ${DISPLAY}`;
  ctx.fillStyle = ink(t.ink);
  lines.forEach((l, i) => ctx.fillText(l, 23, 350 - (lines.length - 1 - i) * size * 0.9));

  // ---- the four ratings
  ctx.fillStyle = face ? t.line : '#fff';
  ctx.fillRect(24, 361, DESIGN_W - 48, 1);
  s.stats.forEach(([, short, v], i) => {
    const cx = 24 + i * ((DESIGN_W - 48 - 26) / 3);
    ctx.font = `600 8px ${SANS}`;
    spaced(ctx, 0.14, 8);
    ctx.fillStyle = ink(t.sub);
    ctx.fillText(short, cx, 378);
    ctx.font = `600 15px ${DISPLAY}`;
    spaced(ctx, 0, 15);
    ctx.fillStyle = ink(t.ink);
    ctx.fillText(String(v), cx, 398);
  });

  outline(ctx, cfg.shape === 'Cut corners', t.rim, face);
};

function drawLayer(w, h, d, draw, mode, blur = 0) {
  const [c, ctx] = canvas(w, h);
  ctx.fillStyle = '#000';
  ctx.fillRect(0, 0, w, h);
  ctx.save();
  ctx.scale(w / DESIGN_W, h / DESIGN_H);
  draw(ctx, d, mode);
  ctx.restore();
  if (blur > 0) {
    const [b, bctx] = canvas(w, h);
    bctx.filter = `blur(${blur}px)`;
    bctx.drawImage(c, 0, 0);
    return b;
  }
  return c;
}

function channel(c) {
  const px = c.getContext('2d').getImageData(0, 0, c.width, c.height).data;
  const out = new Float32Array(c.width * c.height);
  for (let i = 0; i < out.length; i++) out[i] = px[i * 4] / 255;
  return out;
}

export async function createCard(renderer, initial) {
  await loadFonts();
  const aniso = Math.min(8, renderer.capabilities.getMaxAnisotropy());

  const [faceC] = canvas(FACE_W, FACE_H);
  const [normalC, normalCtx] = canvas(MAP_W, MAP_H);
  const [surfC, surfCtx] = canvas(MAP_W, MAP_H);
  const tex = (c, srgb) => {
    const t = new THREE.CanvasTexture(c);
    t.colorSpace = srgb ? THREE.SRGBColorSpace : THREE.NoColorSpace;
    t.anisotropy = aniso;
    return t;
  };
  const faceTex = tex(faceC, true);
  const normalTex = tex(normalC, false);
  const surfTex = tex(surfC, false);

  const holo = { value: 0 };
  const shine = { value: 0 };
  const time = { value: 0 };
  const faceMat = new THREE.MeshPhysicalMaterial({
    map: faceTex,
    normalMap: normalTex,
    roughnessMap: surfTex,
    metalnessMap: surfTex,
    roughness: 1,
    metalness: 1,
    // a little self-light keeps the print readable on the dark stock
    emissive: new THREE.Color(1, 1, 1),
    emissiveMap: faceTex,
  });
  faceMat.onBeforeCompile = (shader) => {
    shader.uniforms.holoStrength = holo;
    shader.uniforms.holoShine = shine;
    shader.uniforms.holoTime = time;
    shader.fragmentShader = shader.fragmentShader
      .replace(
        '#include <common>',
        '#include <common>\nuniform float holoStrength;\nuniform float holoShine;\nuniform float holoTime;',
      )
      .replace(
        '#include <emissivemap_fragment>',
        `#include <emissivemap_fragment>
  {
    // the blue print (the "DNA" of the wordmark) glows a little, like the page's DNA: only bright,
    // clearly blue texels qualify, so the gold type and the (now blue) metal are untouched
    float idBlue = smoothstep( 0.2, 0.45, emissiveColor.b - max( emissiveColor.r, emissiveColor.g ) )
      * smoothstep( 0.55, 0.85, emissiveColor.b );
    totalEmissiveRadiance += emissiveColor.rgb * idBlue * 1.4;
  }
  {
    // reverse-holo band (cards-css reverse.css): a soft diagonal sheen that slides as the card turns
    vec2 ptr = clamp( normal.xy * 1.8, -1.0, 1.0 );
    float diag = ( vMapUv.x - ( 1.0 - vMapUv.y ) ) * 0.7071;
    float band = exp( -pow( ( diag - ( ptr.x - ptr.y ) * 0.55 + sin( holoTime * 0.25 ) * 0.04 ) * 9.0, 2.0 ) );
    vec3 prism = 0.78 + 0.22 * cos( 6.2832 * ( diag * 2.2 + vec3( 0.0, 0.33, 0.67 ) ) );
    totalEmissiveRadiance += prism * band * holoShine * holoStrength * ( 0.15 + diffuseColor.rgb );
  }`,
      );
  };
  const edgeMat = new THREE.MeshPhysicalMaterial({ metalness: 1, roughness: 0.28, envMapIntensity: 0.7 });

  let geo = cardGeometry(initial.shape === 'Cut corners');
  const mesh = new THREE.Mesh(geo, [faceMat, edgeMat]);
  const group = new THREE.Group();
  group.add(mesh);

  let current = initial;
  let shapeKey = initial.shape;

  function render(cfg) {
    const d = { cfg };
    const stealth = cfg.style === 'Stealth';
    const draw = stealth ? drawStealth : drawPlayer;
    const n = MAP_W * MAP_H;

    // colour
    const fctx = faceC.getContext('2d');
    fctx.save();
    fctx.scale(FACE_W / DESIGN_W, FACE_H / DESIGN_H);
    draw(fctx, d, 'face');
    fctx.restore();

    // relief: only the etched symbol, recessed, with a crisp lip (the Player card is flat print)
    const nd = normalCtx.createImageData(MAP_W, MAP_H);
    if (stealth) {
      const etch = channel(drawLayer(MAP_W, MAP_H, d, draw, 'etch', 1.3));
      const S = 1.6;
      for (let y = 0; y < MAP_H; y++) {
        const yu = y > 0 ? y - 1 : y;
        const yd = y < MAP_H - 1 ? y + 1 : y;
        for (let x = 0; x < MAP_W; x++) {
          const xl = x > 0 ? x - 1 : x;
          const xr = x < MAP_W - 1 ? x + 1 : x;
          // height = -etch (recessed)
          const nx = (etch[y * MAP_W + xr] - etch[y * MAP_W + xl]) * S;
          const ny = -(etch[yd * MAP_W + x] - etch[yu * MAP_W + x]) * S; // canvas y runs down, texture v runs up
          const inv = 1 / Math.sqrt(nx * nx + ny * ny + 1);
          const o = (y * MAP_W + x) * 4;
          nd.data[o] = (nx * inv * 0.5 + 0.5) * 255;
          nd.data[o + 1] = (ny * inv * 0.5 + 0.5) * 255;
          nd.data[o + 2] = (inv * 0.5 + 0.5) * 255;
          nd.data[o + 3] = 255;
        }
      }
    } else {
      for (let i = 0; i < n; i++) nd.data.set([128, 128, 255, 255], i * 4);
    }
    normalCtx.putImageData(nd, 0, 0);

    // roughness (G) / metalness (B)
    const sd = surfCtx.createImageData(MAP_W, MAP_H);
    if (stealth) {
      const t = STEALTH[cfg.tier];
      const etch = channel(drawLayer(MAP_W, MAP_H, d, draw, 'etch', 0.6));
      const ink = channel(drawLayer(MAP_W, MAP_H, d, draw, 'ink', 0));
      for (let y = 0; y < MAP_H; y++) {
        for (let x = 0; x < MAP_W; x++) {
          const i = y * MAP_W + x;
          // brushed: roughness streaks across the card; etched = matte, duller metal; ink = printed, not metal
          let rough = t.rough + streak(x / MAP_W) * 0.07;
          let metal = t.metal;
          rough += (0.62 - rough) * etch[i];
          metal += (t.metal * 0.35 - metal) * etch[i];
          rough += (0.5 - rough) * ink[i];
          metal *= 1 - ink[i];
          const o = i * 4;
          sd.data[o] = 255;
          sd.data[o + 1] = Math.max(0, Math.min(255, rough * 255));
          sd.data[o + 2] = Math.max(0, Math.min(255, metal * 255));
          sd.data[o + 3] = 255;
        }
      }
    } else {
      // printed card stock under a satin lacquer (the clearcoat)
      for (let i = 0; i < n; i++) sd.data.set([255, 0.5 * 255, 0, 255], i * 4);
    }
    surfCtx.putImageData(sd, 0, 0);

    faceTex.needsUpdate = true;
    normalTex.needsUpdate = true;
    surfTex.needsUpdate = true;

    // only switching clearcoat / anisotropy on or off changes the shader; values alone don't
    const clearcoat = stealth ? 0 : 0.45;
    const anisotropy = stealth ? 0.55 : 0;
    const recompile = faceMat.anisotropy > 0 !== anisotropy > 0 || faceMat.clearcoat > 0 !== clearcoat > 0;
    faceMat.clearcoat = clearcoat;
    faceMat.clearcoatRoughness = 0.26;
    faceMat.anisotropy = anisotropy;
    faceMat.anisotropyRotation = Math.PI / 2; // brushed down the card
    faceMat.envMapIntensity = stealth ? STEALTH[cfg.tier].env : 0.45;
    // the print glows a touch so it reads in the dark room; the dark stock barely changes
    faceMat.emissiveIntensity = stealth ? (cfg.tier === 'Elite' ? 0.05 : 0.11) : 0.09;
    if (recompile) faceMat.needsUpdate = true;
    shine.value = stealth ? 0 : PLAYER[cfg.tier].holo;
    edgeMat.color.set(stealth ? STEALTH[cfg.tier].rim : PLAYER[cfg.tier].rim);

    if (cfg.shape !== shapeKey) {
      shapeKey = cfg.shape;
      const old = geo;
      geo = cardGeometry(cfg.shape === 'Cut corners');
      mesh.geometry = geo;
      old.dispose();
    }
  }

  render(initial);

  let pending;
  return {
    group,
    update(t) {
      time.value = t;
    },
    setHolo(v) {
      holo.value = v;
    },
    setConfig(cfg) {
      current = cfg;
      // typing fires a lot; recomputing the maps is ~tens of ms, so coalesce
      window.clearTimeout(pending);
      pending = window.setTimeout(() => render(current), 60);
    },
    dispose() {
      window.clearTimeout(pending);
      geo.dispose();
      faceMat.dispose();
      edgeMat.dispose();
      faceTex.dispose();
      normalTex.dispose();
      surfTex.dispose();
    },
  };
}
